import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { FiSave, FiX, FiTrash2, FiCheck, FiInfo, FiUploadCloud, FiImage, FiChevronDown } from 'react-icons/fi';
import { getFieldTypes, FIELD_CATEGORIES } from './fieldTypes';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '../../components/ui/select.jsx';
import { useSelector } from 'react-redux';
import SublyApi from '../../helpers/Api';

// Predefined file type categories
const FILE_TYPE_CATEGORIES = {
	'Images': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'],
	'Documents': ['.pdf', '.doc', '.docx', '.txt', '.rtf'],
	'Spreadsheets': ['.csv', '.xls', '.xlsx'],
	'Presentations': ['.ppt', '.pptx'],
	'Archives': ['.zip', '.rar', '.7z'],
	'Audio': ['.mp3', '.wav', '.ogg', '.m4a'],
	'Video': ['.mp4', '.avi', '.mov', '.wmv', '.flv']
};

const FormFieldEditor = ({ field, sections, onSave, onCancel, onDelete, existingFieldKeys = [] }) => {
	const token = useSelector((state) => state.admin.token);
	const isEditMode = Boolean(field);
	const [availableFieldTypes, setAvailableFieldTypes] = useState({
		all: [],
		grouped: {},
	});
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState('basic'); // Added for multi-step modal

	// Initialize field data state
	const [fieldData, setFieldData] = useState({
		field_key: '',
		type: field?.type || '',
		label: field?.label || '',
		placeholder: '',
		description: '',
		is_required: field?.required || false,
		section_id: field?.section_id || null,
		options: [],
		validation_rules: {
			min: '',
			max: '',
			pattern: '',
			errorMessage: '',
			customValidation: '',
			dateRange: {
				min: '',
				max: '',
			},
			fileTypes: [],
			maxFileSize: '',
			minLength: '',
			maxLength: '',
			numberRange: {
				min: '',
				max: '',
			},
		},
		conditional_logic: {
			enabled: false,
			conditions: [],
			action: 'show',
			operator: 'and',
		},
		appearance: {
			size: 'default',
			width: 'full',
			labelPosition: 'top',
			helpText: '',
			placeholder: '',
			defaultValue: '',
			className: '',
		},
	});

	const [optionInput, setOptionInput] = useState('');
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [fieldKeyError, setFieldKeyError] = useState('');
	const [uploadingOptionIndex, setUploadingOptionIndex] = useState(null);
	const [optionImageError, setOptionImageError] = useState(null);

	// Load field types from API
	useEffect(() => {
		const loadFieldTypes = async () => {
			try {
				const types = await getFieldTypes(token);
				setAvailableFieldTypes(types);

				if (!fieldData.type && types.all.length > 0) {
					const defaultType = types.all[0].id;
					setFieldData((prev) => ({
						...prev,
						type: defaultType,
					}));
				}
				setLoading(false);
			} catch (error) {
				console.error('Error loading field types:', error);
				setLoading(false);
			}
		};
		loadFieldTypes();
	}, [token]);

	// Load field data when editing existing field
	useEffect(() => {
		setFieldKeyError('');
		if (field && field.id) {
			// Parse options if they exist and are a string
			let fieldOptions = field.options;
			if (typeof fieldOptions === 'string' && fieldOptions) {
				try {
					fieldOptions = JSON.parse(fieldOptions);
				} catch (e) {
					console.error('Error parsing field options:', e);
				}
			}

			// Parse validation rules if they exist and are a string
			let validationRules = field.validation_rules;
			if (typeof validationRules === 'string' && validationRules) {
				try {
					validationRules = JSON.parse(validationRules);
				} catch (e) {
					console.error('Error parsing validation rules:', e);
				}
			}

			// Parse conditional logic if it exists and is a string
			let conditionalLogic = field.conditional_logic;
			if (typeof conditionalLogic === 'string' && conditionalLogic) {
				try {
					conditionalLogic = JSON.parse(conditionalLogic);
				} catch (e) {
					console.error('Error parsing conditional logic:', e);
				}
			}

			// Parse appearance if it exists and is a string
			let appearance = field.appearance;
			if (typeof appearance === 'string' && appearance) {
				try {
					appearance = JSON.parse(appearance);
				} catch (e) {
					console.error('Error parsing appearance:', e);
					// Provide default values if parsing fails
					appearance = {
						size: 'default',
						width: 'full',
					};
				}
			} else if (!appearance || typeof appearance !== 'object') {
				// Ensure we always have an appearance object with defaults
				appearance = {
					size: 'default',
					width: 'full',
				};
			}

			// Clean up fileTypes if they exist in validation_rules
			if (validationRules?.fileTypes) {
				const cleanedFileTypes = validationRules.fileTypes.filter(type => {
					// Only keep valid file extensions (start with . and have 2-4 chars after)
					return typeof type === 'string' && type.match(/^\.[a-z]{2,4}$/i);
				});
				
				// Create a new validation_rules object to avoid read-only issues
				validationRules = {
					...validationRules,
					fileTypes: cleanedFileTypes
				};
			}

			setFieldData({
				...field,
				options: fieldOptions || [],
				validation_rules: validationRules || {},
				conditional_logic: conditionalLogic || {},
				appearance: appearance || {
					size: 'default',
					width: 'full',
				},
			});
		}
	}, [field]);

	// Generate field key from label
	const generateFieldKey = (label) => {
		return label
			.toLowerCase()
			.replace(/[^a-z0-9_]/g, '_')
			.replace(/_+/g, '_')
			.replace(/^_|_$/g, '');
	};

	const validateFieldKey = (key) => {
		if (!key || !key.trim()) {
			setFieldKeyError('Field key is required.');
			return false;
		}
		if (existingFieldKeys.map(k => k?.toLowerCase()).includes(key?.toLowerCase())) {
			setFieldKeyError(`A field with the key "${key}" already exists. Use a different label or field key.`);
			return false;
		}
		setFieldKeyError('');
		return true;
	};

	// Handle field input changes
	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;

		// Auto-generate field_key when label changes (only if field_key is empty or matches previous auto-generated value)
		if (name === 'label') {
			const generatedKey = generateFieldKey(value);
			const shouldUpdateKey =
				!fieldData.field_key ||
				fieldData.field_key === generateFieldKey(fieldData.label);

			const newKey = shouldUpdateKey ? generatedKey : fieldData.field_key;
			validateFieldKey(newKey);

			setFieldData({
				...fieldData,
				label: value,
				...(shouldUpdateKey ? { field_key: generatedKey } : {}),
			});
		} else if (name === 'field_key') {
			validateFieldKey(value);
			setFieldData({
				...fieldData,
				field_key: value,
			});
		} else {
			setFieldData({
				...fieldData,
				[name]: type === 'checkbox' ? checked : value,
			});
		}
	};

	// Handle changes to the validation rules
	const handleValidationChange = (e) => {
		const { name, value } = e.target;
		setFieldData({
			...fieldData,
			validation_rules: {
				...fieldData.validation_rules,
				[name]: value,
			},
		});
	};

	// Handle changes to appearance settings
	const handleAppearanceChange = (e) => {
		const { name, value } = e.target;

		const updatedAppearance = {
			...fieldData.appearance,
			[name]: value,
		};

		// Update the entire fieldData with new appearance
		setFieldData({
			...fieldData,
			appearance: updatedAppearance,
		});
	};

	// Add option to options array (for select, checkbox, radio)
	const handleAddOption = () => {
		if (optionInput.trim()) {
			setFieldData({
				...fieldData,
				options: [
					...fieldData.options,
					{
						value: generateFieldKey(optionInput),
						label: optionInput.trim(),
					},
				],
			});
			setOptionInput('');
		}
	};

	// Remove option from options array
	const handleRemoveOption = (index) => {
		const newOptions = [...fieldData.options];
		newOptions.splice(index, 1);
		setFieldData({
			...fieldData,
			options: newOptions,
		});
	};

	// Toggle enable images for radio/checkbox/multiselect fields
	const handleToggleEnableImages = () => {
		setFieldData({
			...fieldData,
			appearance: {
				...fieldData.appearance,
				enableImages: !fieldData.appearance?.enableImages,
			},
		});
	};

	// Upload image for a specific option
	const handleOptionImageUpload = async (index, file) => {
		const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
		if (file.size > MAX_UPLOAD_SIZE) {
			setOptionImageError({ index, message: 'File must be under 10MB' });
			return;
		}
		setOptionImageError(null);
		setUploadingOptionIndex(index);
		try {
			const formDataObj = new FormData();
			formDataObj.append('file', file);

			const response = await SublyApi.request(
				'api/admin/forms/upload-file',
				formDataObj,
				'post',
				{
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				}
			);

			const newOptions = [...fieldData.options];
			newOptions[index] = { ...newOptions[index], imageUrl: response.url };
			setFieldData({ ...fieldData, options: newOptions });
		} catch (err) {
			console.error('Error uploading option image:', err);
			setOptionImageError({ index, message: 'Failed to upload image' });
		}
		setUploadingOptionIndex(null);
	};

	// Remove image from a specific option
	const handleRemoveOptionImage = (index) => {
		const newOptions = [...fieldData.options];
		const { imageUrl, ...rest } = newOptions[index];
		newOptions[index] = rest;
		setFieldData({ ...fieldData, options: newOptions });
	};

	// Handle save button click
	const handleSave = () => {
		// Validate field_key uniqueness before saving
		if (!validateFieldKey(fieldData.field_key)) {
			setActiveTab('basic');
			return;
		}

		const finalFieldData = { ...fieldData };

		// Format options as JSON string if it's an array
		if (Array.isArray(finalFieldData.options)) {
			finalFieldData.options = JSON.stringify(finalFieldData.options);
		}

		// Format validation rules as JSON string if it's an object
		if (
			typeof finalFieldData.validation_rules === 'object' &&
			finalFieldData.validation_rules !== null
		) {
			// Clean fileTypes before saving to ensure only valid extensions are saved
			if (finalFieldData.validation_rules.fileTypes) {
				const cleanedFileTypes = finalFieldData.validation_rules.fileTypes.filter(type => {
					// Only keep valid file extensions (start with . and have 2-4 chars after)
					return typeof type === 'string' && type.match(/^\.[a-z]{2,4}$/i);
				});
				
				// Create a new validation_rules object to avoid read-only issues
				finalFieldData.validation_rules = {
					...finalFieldData.validation_rules,
					fileTypes: cleanedFileTypes
				};
			}
			
			finalFieldData.validation_rules = JSON.stringify(
				finalFieldData.validation_rules
			);
		}

		// Format conditional logic as JSON string if it's an object
		if (
			typeof finalFieldData.conditional_logic === 'object' &&
			finalFieldData.conditional_logic !== null
		) {
			finalFieldData.conditional_logic = JSON.stringify(
				finalFieldData.conditional_logic
			);
		}

		// Format appearance settings as JSON string if it's an object
		if (
			typeof finalFieldData.appearance === 'object' &&
			finalFieldData.appearance !== null
		) {
			finalFieldData.appearance = JSON.stringify(finalFieldData.appearance);
		}

		onSave(finalFieldData);
	};

	// Luxury input classes used throughout the field editor
	const inputClasses = `w-full px-4 py-3 border border-gray-200 bg-white rounded-lg shadow-sm
		font-montserrat-regular text-gray-900 placeholder-gray-400
		focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 focus:bg-white focus:shadow-md
		hover:border-gray-400 hover:shadow-sm
		transition-all duration-200 ease-in-out`;

	// Add validation rules based on field type
	const getValidationFields = (type) => {
		const commonFields = (
			<div className='group'>
				<Label htmlFor='errorMessage' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Custom Error Message
				</Label>
				<Input
					id='errorMessage'
					name='errorMessage'
					value={fieldData.validation_rules.errorMessage}
					onChange={handleValidationChange}
					placeholder='Enter a custom error message for validation failures'
					className={inputClasses}
				/>
				<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
					Displayed when validation fails (optional)
				</p>
			</div>
		);

		switch (type) {
			case 'text':
			case 'textarea':
				return (
					<div className='flex flex-col gap-6'>
						{commonFields}
						<div className='grid grid-cols-2 gap-4'>
							<div className='group'>
								<Label htmlFor='minLength' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Minimum Length
								</Label>
								<Input
									id='minLength'
									name='minLength'
									type='number'
									value={fieldData.validation_rules.minLength}
									onChange={handleValidationChange}
									placeholder='e.g., 10'
									className={inputClasses}
								/>
							</div>
							<div className='group'>
								<Label htmlFor='maxLength' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Maximum Length
								</Label>
								<Input
									id='maxLength'
									name='maxLength'
									type='number'
									value={fieldData.validation_rules.maxLength}
									onChange={handleValidationChange}
									placeholder='e.g., 500'
									className={inputClasses}
								/>
							</div>
						</div>
						<div className='group'>
							<Label htmlFor='pattern' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
								Pattern (Regex)
							</Label>
							<Input
								id='pattern'
								name='pattern'
								value={fieldData.validation_rules.pattern}
								onChange={handleValidationChange}
								placeholder='Regular expression pattern'
								className={inputClasses}
							/>
							<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
								Advanced: Use regex for custom validation patterns
							</p>
						</div>
					</div>
				);

			case 'number':
			case 'price':
				return (
					<div className='flex flex-col gap-6'>
						{commonFields}
						<div className='grid grid-cols-2 gap-4'>
							<div className='group'>
								<Label htmlFor='numberRange.min' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Minimum Value
								</Label>
								<Input
									id='numberRange.min'
									name='numberRange.min'
									type='number'
									value={fieldData.validation_rules.numberRange?.min}
									onChange={handleValidationChange}
									placeholder='e.g., 0'
									className={inputClasses}
								/>
							</div>
							<div className='group'>
								<Label htmlFor='numberRange.max' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Maximum Value
								</Label>
								<Input
									id='numberRange.max'
									name='numberRange.max'
									type='number'
									value={fieldData.validation_rules.numberRange?.max}
									onChange={handleValidationChange}
									placeholder='e.g., 1000000'
									className={inputClasses}
								/>
							</div>
						</div>
					</div>
				);

			case 'date':
				return (
					<div className='flex flex-col gap-6'>
						{commonFields}
						<div className='grid grid-cols-2 gap-4'>
							<div className='group'>
								<Label htmlFor='dateRange.min' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Earliest Date
								</Label>
								<Input
									id='dateRange.min'
									name='dateRange.min'
									type='date'
									value={fieldData.validation_rules.dateRange?.min}
									onChange={handleValidationChange}
									className={inputClasses}
								/>
							</div>
							<div className='group'>
								<Label htmlFor='dateRange.max' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
									Latest Date
								</Label>
								<Input
									id='dateRange.max'
									name='dateRange.max'
									type='date'
									value={fieldData.validation_rules.dateRange?.max}
									onChange={handleValidationChange}
									className={inputClasses}
								/>
							</div>
						</div>
					</div>
				);

			case 'file':
			case 'image':
				// Helper function to handle file type category selection
				const handleFileTypeCategoryChange = (category, isChecked) => {
					const categoryTypes = FILE_TYPE_CATEGORIES[category];
					let currentTypes = [...(fieldData.validation_rules.fileTypes || [])];

					if (isChecked) {
						// Add all types from this category that aren't already included
						categoryTypes.forEach(type => {
							if (!currentTypes.includes(type)) {
								currentTypes.push(type);
							}
						});
					} else {
						// Remove all types from this category
						currentTypes = currentTypes.filter(type => !categoryTypes.includes(type));
					}

					handleValidationChange({
						target: {
							name: 'fileTypes',
							value: currentTypes
						}
					});
				};

				// Helper function to check if a category is fully selected
				const isCategorySelected = (category) => {
					const categoryTypes = FILE_TYPE_CATEGORIES[category];
					const currentTypes = fieldData.validation_rules.fileTypes || [];
					return categoryTypes.every(type => currentTypes.includes(type));
				};

				// Helper function to check if a category is partially selected
				const isCategoryPartiallySelected = (category) => {
					const categoryTypes = FILE_TYPE_CATEGORIES[category];
					const currentTypes = fieldData.validation_rules.fileTypes || [];
					return categoryTypes.some(type => currentTypes.includes(type)) && !isCategorySelected(category);
				};

				const isImageType = type === 'image';

				return (
					<div className='flex flex-col gap-6'>
						{commonFields}
						<div className='group'>
							<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
								Allowed File Types
							</Label>
							<p className='text-sm font-montserrat-regular text-gray-400 mb-3 leading-relaxed'>
								Select the types of files users can upload
							</p>
							<div className='space-y-2 border border-gray-200 rounded-xl p-4 max-h-64 overflow-y-auto bg-white hover:shadow-sm transition-shadow duration-200'>
								{Object.entries(FILE_TYPE_CATEGORIES).map(([category, types]) => {
									const isSelected = isCategorySelected(category);
									const isPartial = isCategoryPartiallySelected(category);
									return (
										<label
											key={category}
											htmlFor={`category-${category}`}
											className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
												${isSelected
													? 'border-gray-900 bg-gray-50 shadow-sm'
													: isPartial
													? 'border-primary/50 bg-pink-50/30'
													: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
												}
											`}
										>
											<Checkbox
												id={`category-${category}`}
												checked={isSelected}
												onCheckedChange={(isChecked) => handleFileTypeCategoryChange(category, isChecked)}
												className='sr-only'
											/>
											<div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center transition-all duration-200
												${isSelected ? 'border-gray-900 bg-gray-900' : isPartial ? 'border-primary bg-primary/20' : 'border-gray-300 bg-white'}
											`}>
												{isSelected && <FiCheck className='h-3 w-3 text-white' />}
												{isPartial && !isSelected && <div className='w-2 h-2 bg-primary rounded-sm' />}
											</div>
											<div className='flex-1'>
												<span className={`font-montserrat-medium text-sm ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
													{category}
												</span>
												<p className='text-xs font-montserrat-regular text-gray-400 mt-1'>
													{types.join(', ')}
												</p>
											</div>
											{isSelected && (
												<div className='absolute top-2 right-2'>
													<FiCheck className='h-4 w-4 text-gray-900' />
												</div>
											)}
										</label>
									);
								})}
							</div>

							{/* Show currently selected types */}
							{fieldData.validation_rules.fileTypes && fieldData.validation_rules.fileTypes.length > 0 && (
								<div className={`mt-4 p-4 rounded-lg border ${isImageType ? 'border-pink-100 bg-pink-50/50' : 'border-gray-100 bg-gray-50'}`}>
									<p className='text-sm font-montserrat-medium text-gray-700 mb-2'>Selected types:</p>
									<div className='flex flex-wrap gap-2'>
										{fieldData.validation_rules.fileTypes.map((type, idx) => (
											<span
												key={idx}
												className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-montserrat-medium ${
													isImageType ? 'bg-pink-100 text-primary' : 'bg-gray-200 text-gray-700'
												}`}
											>
												{type}
											</span>
										))}
									</div>
								</div>
							)}
						</div>
						<div className='group'>
							<Label htmlFor='maxFileSize' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
								Maximum File Size (MB)
							</Label>
							<Input
								id='maxFileSize'
								name='maxFileSize'
								type='number'
								value={fieldData.validation_rules.maxFileSize}
								onChange={handleValidationChange}
								placeholder='e.g., 3'
								className={inputClasses}
							/>
							<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
								Recommended: 3MB for most uploads
							</p>
						</div>
					</div>
				);

			default:
				return commonFields;
		}
	};

	// Handle field type change
	const handleFieldTypeChange = (value) => {
		setFieldData((prev) => ({
			...prev,
			type: value,
			// Reset field-specific data when type changes
			options: [],
			validation_rules: {
				...prev.validation_rules,
				minLength: '',
				maxLength: '',
				pattern: '',
				errorMessage: '',
				customValidation: '',
				dateRange: { min: '', max: '' },
				fileTypes: [],
				maxFileSize: '',
				numberRange: { min: '', max: '' },
			},
		}));
	};

	if (loading) {
		return <div>Loading field types...</div>;
	}

	// Render the basic settings tab
	const renderBasicSettings = () => (
		<div className='flex flex-col gap-6'>
			{/* Field Type Selector */}
			<div className='group'>
				<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Field Type
				</Label>
				<Select value={fieldData.type} onValueChange={handleFieldTypeChange}>
					<SelectTrigger className={inputClasses}>
						<SelectValue placeholder='Select a field type'>
							{fieldData.type &&
								availableFieldTypes.all?.find((ft) => ft.id === fieldData.type)
									?.label}
						</SelectValue>
					</SelectTrigger>
					<SelectContent className='max-h-[300px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg'>
						{Object.entries(FIELD_CATEGORIES).map(([key, category]) => (
							<SelectGroup key={category}>
								<SelectLabel className='capitalize font-montserrat-medium px-4 py-2 text-sm bg-gray-50 text-gray-700 tracking-wide'>
									{category}
								</SelectLabel>
								{availableFieldTypes.grouped[category]?.map((fieldType) => (
									<SelectItem
										key={fieldType.id}
										value={fieldType.id}
										className='relative flex w-full items-center py-2.5 pl-8 pr-4 cursor-pointer font-montserrat-regular text-gray-700 hover:bg-gray-50 transition-colors duration-150'>
										{fieldType.label}
									</SelectItem>
								))}
							</SelectGroup>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Label Field */}
			<div className='group'>
				<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Field Title
				</Label>
				<Input
					name='label'
					value={fieldData.label}
					onChange={handleInputChange}
					placeholder='Enter field title'
					className={inputClasses}
				/>
				<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
					The title displayed above the field
				</p>
			</div>

			{/* Field Key */}
			<div className='group'>
				<Label htmlFor='field_key' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Field Key
				</Label>
				<Input
					id='field_key'
					name='field_key'
					value={fieldData.field_key}
					onChange={handleInputChange}
					placeholder='Field identifier'
					className={`${inputClasses} ${fieldKeyError ? 'border-red-400 focus:ring-red-400/20 focus:border-red-500' : ''}`}
				/>
				<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
					Unique identifier used in form data
				</p>
				{fieldKeyError && (
					<div className='mt-2 flex items-start bg-red-50 border border-red-100 rounded-md px-3 py-2'>
						<FiInfo className='mr-2 h-4 w-4 text-red-500 flex-shrink-0 mt-0.5' />
						<p className='text-sm text-red-600 font-montserrat-regular'>
							{fieldKeyError}
						</p>
					</div>
				)}
			</div>

			{/* Description */}
			<div className='group'>
				<Label htmlFor='description' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Description
				</Label>
				<textarea
					id='description'
					name='description'
					value={fieldData.description}
					onChange={handleInputChange}
					placeholder='Help text for this field'
					rows={3}
					className={`${inputClasses} resize-none`}
				/>
				<div className='mt-2 flex justify-between items-center'>
					<p className='text-sm font-montserrat-regular text-gray-400 leading-relaxed'>
						Optional help text shown below the field
					</p>
					<span className='text-xs font-montserrat-regular text-gray-400'>
						{fieldData.description?.length || 0} characters
					</span>
				</div>
			</div>

			{/* Section Selector */}
			<div className='group'>
				<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
					Section
				</Label>
				<Select
					value={fieldData.section_id?.toString() || 'none'}
					onValueChange={(value) =>
						setFieldData((prev) => ({
							...prev,
							section_id: value === 'none' ? null : parseInt(value, 10),
						}))
					}>
					<SelectTrigger className={inputClasses}>
						<SelectValue placeholder='Select a section (optional)' />
					</SelectTrigger>
					<SelectContent className='bg-white border border-gray-200 rounded-lg shadow-lg'>
						<SelectItem value='none' className='py-2.5 px-4 font-montserrat-regular text-gray-700 hover:bg-gray-50'>
							No Section
						</SelectItem>
						{sections?.map((section) => (
							<SelectItem
								key={section.id}
								value={section.id.toString()}
								className='py-2.5 px-4 font-montserrat-regular text-gray-700 hover:bg-gray-50'
							>
								{section.title}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Required Field Checkbox - Card Style */}
			<div className='group'>
				<label
					htmlFor='is_required'
					className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
						${fieldData.is_required
							? 'border-gray-900 bg-gray-50 shadow-sm'
							: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
						}
					`}
				>
					<input
						type='checkbox'
						id='is_required'
						name='is_required'
						checked={fieldData.is_required}
						onChange={handleInputChange}
						className='sr-only'
					/>
					<div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 flex items-center justify-center transition-all duration-200
						${fieldData.is_required ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
					`}>
						{fieldData.is_required && (
							<FiCheck className='h-3 w-3 text-white' />
						)}
					</div>
					<div>
						<span className={`font-montserrat-regular text-sm ${fieldData.is_required ? 'text-gray-900 font-montserrat-medium' : 'text-gray-700'}`}>
							Required Field
						</span>
						<p className='text-xs font-montserrat-regular text-gray-400 mt-0.5'>
							Users must fill in this field to submit the form
						</p>
					</div>
					{fieldData.is_required && (
						<div className='absolute top-2 right-2'>
							<FiCheck className='h-4 w-4 text-gray-900' />
						</div>
					)}
				</label>
			</div>

			{/* Enable Images Toggle - Card Style (radio, checkbox, multiselect only) */}
			{['radio', 'checkbox', 'multiselect'].includes(fieldData.type) && (
				<div className='group'>
					<label
						htmlFor='enableImages'
						className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
							${fieldData.appearance?.enableImages
								? 'border-gray-900 bg-gray-50 shadow-sm'
								: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
							}
						`}
					>
						<input
							type='checkbox'
							id='enableImages'
							checked={fieldData.appearance?.enableImages || false}
							onChange={handleToggleEnableImages}
							className='sr-only'
						/>
						<div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 flex items-center justify-center transition-all duration-200
							${fieldData.appearance?.enableImages ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
						`}>
							{fieldData.appearance?.enableImages && (
								<FiCheck className='h-3 w-3 text-white' />
							)}
						</div>
						<div>
							<span className={`font-montserrat-regular text-sm ${fieldData.appearance?.enableImages ? 'text-gray-900 font-montserrat-medium' : 'text-gray-700'}`}>
								Enable Images
							</span>
							<p className='text-xs font-montserrat-regular text-gray-400 mt-0.5'>
								Add a photo to each option for visual selection cards
							</p>
						</div>
						{fieldData.appearance?.enableImages && (
							<div className='absolute top-2 right-2'>
								<FiImage className='h-4 w-4 text-gray-900' />
							</div>
						)}
					</label>
				</div>
			)}

			{/* Options for select, multiselect, checkbox, radio */}
			{['select', 'multiselect', 'checkbox', 'radio'].includes(fieldData.type) && (
				<div className='group'>
					<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
						Options
					</Label>
					<div className='flex gap-3 mb-4'>
						<Input
							placeholder='Add option'
							value={optionInput}
							onChange={(e) => setOptionInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									handleAddOption();
								}
							}}
							className={inputClasses}
						/>
						<Button
							type='button'
							onClick={handleAddOption}
							variant='outline'
							className='px-6 py-3 border-2 border-gray-900 text-gray-900 font-montserrat-medium rounded-lg hover:bg-gray-900 hover:text-white transition-all duration-200'
						>
							Add
						</Button>
					</div>
					<div className='space-y-2'>
						{fieldData.options.map((option, index) => {
							const showImageUpload = fieldData.appearance?.enableImages && ['radio', 'checkbox', 'multiselect'].includes(fieldData.type);
							const isUploading = uploadingOptionIndex === index;

							return (
								<div
									key={index}
									className={`bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-all duration-200 ${showImageUpload ? 'p-3' : 'p-4'}`}>
									<div className='flex items-center justify-between'>
										<div className='flex items-center'>
											<div className='w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0'>
												<span className='text-sm font-montserrat-medium text-gray-600'>{index + 1}</span>
											</div>
											<div>
												<span className='font-montserrat-medium text-gray-900'>{option.label}</span>
												<span className='text-gray-400 text-sm ml-2 font-montserrat-regular'>
													({option.value})
												</span>
											</div>
										</div>
										<button
											type='button'
											onClick={() => handleRemoveOption(index)}
											className='p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200'>
											<FiX className='h-4 w-4' />
										</button>
									</div>

									{/* Image upload area — only when Enable Images is ON */}
									{showImageUpload && (
										<div className='mt-3 ml-11'>
											{isUploading ? (
												<div className='flex items-center justify-center h-20 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50'>
													<div className='flex items-center gap-2 text-gray-400'>
														<div className='w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin' />
														<span className='text-xs font-montserrat-regular'>Uploading...</span>
													</div>
												</div>
											) : option.imageUrl ? (
												<div className='relative group/img inline-block'>
													<img
														src={option.imageUrl}
														alt={option.label}
														className='h-20 w-32 object-cover rounded-lg border border-gray-200'
													/>
													<button
														type='button'
														onClick={() => handleRemoveOptionImage(index)}
														className='absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity duration-200 shadow-sm'>
														<FiX className='h-3 w-3' />
													</button>
												</div>
											) : (
												<label className='flex items-center gap-2 h-20 px-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all duration-200'>
													<FiImage className='h-5 w-5 text-gray-400' />
													<span className='text-xs font-montserrat-regular text-gray-400'>Upload Image</span>
													<input
														type='file'
														accept='image/*'
														className='sr-only'
														onChange={(e) => {
															const file = e.target.files?.[0];
															if (file) handleOptionImageUpload(index, file);
															e.target.value = '';
														}}
													/>
												</label>
											)}
											{optionImageError && optionImageError.index === index && (
												<p className='text-xs text-red-500 font-montserrat-regular mt-1'>
													{optionImageError.message}
												</p>
											)}
										</div>
									)}
								</div>
							);
						})}
						{fieldData.options.length === 0 && (
							<div className='text-center py-8 border-2 border-dashed border-gray-200 rounded-lg'>
								<p className='text-sm font-montserrat-regular text-gray-400'>
									No options added yet
								</p>
								<p className='text-xs font-montserrat-regular text-gray-400 mt-1'>
									Add at least one option for this field type
								</p>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);

	// Render the validation tab
	const renderValidationTab = () => (
		<div className='flex flex-col gap-6'>
			<div className='border-l-4 border-l-primary bg-gradient-to-r from-gray-50 to-white px-6 py-4 rounded-r-lg'>
				<h3 className='text-lg font-glacial font-semibold text-gray-900 tracking-wide'>Validation Rules</h3>
				<p className='text-sm font-montserrat-regular text-gray-400 mt-1'>
					Configure validation constraints for this field
				</p>
			</div>
			{getValidationFields(fieldData.type)}
		</div>
	);

	// Render the appearance tab with luxury field preview
	const renderAppearanceTab = () => {
		const isImageField = fieldData.type === 'image';
		const isFileField = fieldData.type === 'file' || isImageField;
		const isSelectField = ['select', 'multiselect', 'checkbox', 'radio'].includes(fieldData.type);

		// Size class based on appearance
		const sizeClass =
			fieldData.appearance.size === 'small'
				? 'text-sm py-2'
				: fieldData.appearance.size === 'large'
				? 'text-base py-4'
				: 'py-3';

		// Preview input classes matching FormPreview luxury styling
		const previewInputClasses = `w-full ${sizeClass} px-4 border border-gray-200 bg-white rounded-lg shadow-sm
			font-montserrat-regular text-gray-900 placeholder-gray-400
			focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 focus:shadow-md
			hover:border-gray-400 hover:shadow-sm
			transition-all duration-200 ease-in-out`;

		return (
			<div className='flex flex-col gap-6'>
				{/* Section Header */}
				<div className='border-l-4 border-l-primary bg-gradient-to-r from-gray-50 to-white px-6 py-4 rounded-r-lg'>
					<h3 className='text-lg font-glacial font-semibold text-gray-900 tracking-wide'>Appearance</h3>
					<p className='text-sm font-montserrat-regular text-gray-400 mt-1'>
						Customize how this field looks in the form
					</p>
				</div>

				{/* Field Size Selector */}
				<div className='group'>
					<Label htmlFor='appearance.size' className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
						Field Size
					</Label>
					<div className='grid grid-cols-3 gap-3'>
						{['small', 'default', 'large'].map((size) => {
							const isSelected = (fieldData.appearance.size || 'default') === size;
							return (
								<label
									key={size}
									className={`relative flex flex-col items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
										${isSelected
											? 'border-gray-900 bg-gray-50 shadow-sm'
											: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
										}
									`}
								>
									<input
										type='radio'
										name='size'
										value={size}
										checked={isSelected}
										onChange={handleAppearanceChange}
										className='sr-only'
									/>
									<div className={`w-full h-${size === 'small' ? '6' : size === 'large' ? '10' : '8'} bg-gray-200 rounded mb-2 transition-all duration-200`}></div>
									<span className={`text-sm font-montserrat-regular capitalize ${isSelected ? 'text-gray-900 font-montserrat-medium' : 'text-gray-600'}`}>
										{size}
									</span>
									{isSelected && (
										<div className='absolute top-2 right-2'>
											<FiCheck className='h-4 w-4 text-gray-900' />
										</div>
									)}
								</label>
							);
						})}
					</div>
					<p className='text-sm font-montserrat-regular text-gray-400 mt-2.5 leading-relaxed'>
						Controls the height and text size of the field
					</p>
				</div>

				{/* Live Field Preview */}
				<div className='group'>
					<Label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide'>
						Live Preview
					</Label>
					<div className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500'>
						{/* Section Header Preview */}
						<div className='border-l-4 border-l-primary bg-gradient-to-r from-gray-50 to-white px-8 py-6'>
							<h3 className='text-lg font-glacial font-semibold text-gray-900 tracking-wide'>
								Sample Section
							</h3>
							<p className='text-sm font-montserrat-regular text-gray-400 mt-2 leading-relaxed'>
								Preview of how your field will appear
							</p>
						</div>
						{/* Field Content Preview */}
						<div className='px-8 py-8'>
							<div className='flex flex-col gap-6'>
								<div className='group'>
									<label className='block text-base font-montserrat-medium text-gray-800 mb-2.5 tracking-wide transition-colors duration-200 group-focus-within:text-gray-900'>
										{fieldData.label || 'Sample Field'}
										{fieldData.is_required && <span className='text-primary ml-1'>*</span>}
									</label>
									<div className='mt-2'>
										{/* Render different preview based on field type */}
										{isFileField ? (
											<div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm ${
												isImageField ? 'hover:border-pink-200' : ''
											}`}>
												<div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-300 ${
													isImageField ? 'bg-pink-50 text-primary' : 'bg-gray-100 text-gray-400'
												}`}>
													{isImageField ? <FiImage className='h-8 w-8' /> : <FiUploadCloud className='h-8 w-8' />}
												</div>
												<div className='space-y-2'>
													<p className='text-base font-montserrat-medium text-gray-900'>
														{isImageField ? 'Drag and drop your image here' : 'Drag and drop your file here'}
													</p>
													<p className='text-sm font-montserrat-regular text-gray-500'>
														or <span className={`underline underline-offset-2 ${isImageField ? 'text-primary' : 'text-gray-900'}`}>browse</span> to choose {isImageField ? 'an image' : 'a file'}
													</p>
												</div>
												<div className='mt-4 pt-4 border-t border-gray-100'>
													<p className={`text-xs font-montserrat-medium ${isImageField ? 'text-primary/70' : 'text-gray-500'}`}>
														{isImageField ? 'Accepted: JPG, PNG, GIF, WebP' : 'All file types accepted'}
													</p>
												</div>
											</div>
										) : isSelectField && fieldData.type === 'select' ? (
											<div className='relative'>
												<button
													type='button'
													className={`${previewInputClasses} flex items-center justify-between`}
													disabled
												>
													<span className='text-gray-400'>Select an option</span>
													<FiChevronDown className='h-5 w-5 text-gray-400' />
												</button>
											</div>
										) : isSelectField && (fieldData.type === 'checkbox' || fieldData.type === 'multiselect') ? (
											<div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
												{(fieldData.options.length > 0 ? fieldData.options.slice(0, 4) : [{label: 'Option 1', value: 'opt1'}, {label: 'Option 2', value: 'opt2'}]).map((option, idx) => (
													<label
														key={idx}
														className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
															${idx === 0
																? 'border-gray-900 bg-gray-50 shadow-sm'
																: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
															}
														`}
													>
														<div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 flex items-center justify-center transition-all duration-200
															${idx === 0 ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
														`}>
															{idx === 0 && <FiCheck className='h-3 w-3 text-white' />}
														</div>
														<span className={`font-montserrat-regular text-sm ${idx === 0 ? 'text-gray-900 font-montserrat-medium' : 'text-gray-700'}`}>
															{option.label}
														</span>
													</label>
												))}
											</div>
										) : isSelectField && fieldData.type === 'radio' ? (
											<div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
												{(fieldData.options.length > 0 ? fieldData.options.slice(0, 4) : [{label: 'Option 1', value: 'opt1'}, {label: 'Option 2', value: 'opt2'}]).map((option, idx) => (
													<label
														key={idx}
														className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out
															${idx === 0
																? 'border-gray-900 bg-gray-50 shadow-sm'
																: 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50/50 hover:shadow-sm'
															}
														`}
													>
														<div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200
															${idx === 0 ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
														`}>
															{idx === 0 && <div className='w-2 h-2 rounded-full bg-white' />}
														</div>
														<span className={`font-montserrat-regular text-sm ${idx === 0 ? 'text-gray-900 font-montserrat-medium' : 'text-gray-700'}`}>
															{option.label}
														</span>
													</label>
												))}
											</div>
										) : fieldData.type === 'textarea' ? (
											<textarea
												placeholder={fieldData.placeholder || 'Enter text here...'}
												rows={3}
												className={`${previewInputClasses} resize-none`}
												disabled
											/>
										) : (
											<input
												type='text'
												placeholder={fieldData.placeholder || 'Enter text here...'}
												className={previewInputClasses}
												disabled
											/>
										)}
									</div>
									{fieldData.description && (
										<p className='text-sm font-montserrat-regular text-gray-400 mt-2.5 leading-relaxed'>
											{fieldData.description}
										</p>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Design System Info */}
				<div className='bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-xl p-6 hover:shadow-sm transition-shadow duration-200'>
					<div className='flex items-start'>
						<div className='w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-4 flex-shrink-0'>
							<FiInfo className='h-5 w-5 text-gray-600' />
						</div>
						<div>
							<h4 className='text-sm font-montserrat-medium text-gray-900'>Jane Rothe Design System</h4>
							<p className='text-sm font-montserrat-regular text-gray-500 mt-1.5 leading-relaxed'>
								This preview uses the official Jane Rothe design system with sophisticated minimalism,
								premium typography (Glacial for titles, Montserrat for body), and a refined black/white
								palette with strategic pink accents.
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden'>
			{/* Header with elegant styling */}
			<div className='border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-6 py-5'>
				<div className='flex justify-between items-center'>
					<div>
						<h2 className='text-xl font-glacial font-semibold text-gray-900 tracking-tight'>
							{isEditMode ? 'Edit Field' : 'Add New Field'}
						</h2>
						<p className='text-sm font-montserrat-regular text-gray-400 mt-1'>
							{isEditMode ? 'Modify field properties and settings' : 'Configure your new form field'}
						</p>
					</div>
					<div className='flex items-center gap-3 flex-wrap justify-end'>
						<Button
							onClick={onCancel}
							variant='outline'
							className='px-4 py-2.5 border border-gray-200 text-gray-600 font-montserrat-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200'
						>
							Cancel
						</Button>
						{isEditMode && (
							<Button
								onClick={onDelete}
								variant='destructive'
								className='px-4 py-2.5 border border-red-200 bg-red-50 text-red-600 font-montserrat-medium rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200'
							>
								<FiTrash2 className='mr-2 h-4 w-4' />
								Delete
							</Button>
						)}
						<Button
							onClick={handleSave}
							variant='primary'
							className='px-6 py-2.5 bg-gray-900 text-white font-montserrat-medium rounded-lg hover:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-200'
						>
							<FiSave className='mr-2 h-4 w-4' />
							Save Field
						</Button>
					</div>
				</div>
			</div>

			{/* Tabs Navigation - Luxury styling */}
			<div className='border-b border-gray-100 px-6'>
				<div className='flex space-x-1'>
					{[
						{ id: 'basic', label: 'Basic Settings' },
						{ id: 'validation', label: 'Validation' },
						{ id: 'appearance', label: 'Appearance' },
					].map((tab) => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id)}
							className={`relative px-5 py-4 font-montserrat-medium text-sm transition-all duration-200
								${activeTab === tab.id
									? 'text-gray-900'
									: 'text-gray-500 hover:text-gray-700'
								}
							`}
						>
							{tab.label}
							{activeTab === tab.id && (
								<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full'></div>
							)}
						</button>
					))}
				</div>
			</div>

			{/* Tab Content - Scrollable Area with elegant padding */}
			<div className='overflow-y-auto max-h-[65vh] px-6 py-6'>
				{activeTab === 'basic' && renderBasicSettings()}
				{activeTab === 'validation' && renderValidationTab()}
				{activeTab === 'appearance' && renderAppearanceTab()}
			</div>
		</div>
	);
};

export default FormFieldEditor;
